import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, RotateCcw, Menu, Volume2, VolumeX, MessageSquarePlus, Sparkles, DollarSign, Phone, Briefcase } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { streamChat } from "@/lib/chatStream";

type Msg = { role: "user" | "assistant"; content: string };

const INITIAL_MSG: Msg = {
  role: "assistant",
  content: "Hey there! 👋 I'm the Xentic AI assistant. Ask me anything about our AI chatbot services, pricing, or how we help businesses capture more leads."
};

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const MAX_MESSAGES = 100;

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"welcome" | "chat">("welcome");
  const [messages, setMessages] = useState<Msg[]>([INITIAL_MSG]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  const resetChat = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([INITIAL_MSG]);
    setConversationId(null);
    setInput("");
    setLoading(false);
  }, []);

  const startNewChat = useCallback(() => {
    resetChat();
    setView("chat");
  }, [resetChat]);

  // Auto-scroll smoothly
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, loading]);

  // Focus input when chat opens
  useEffect(() => {
    if (open && view === "chat") {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, view]);

  // Inactivity auto-reset
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (messages.length > 1) {
        resetChat();
        setMessages([{
          role: "assistant",
          content: "It looks like you stepped away. No worries — I'm here whenever you're ready to chat again! 😊"
        }]);
      }
    }, INACTIVITY_TIMEOUT);
  }, [messages.length, resetChat]);

  useEffect(() => {
    if (open) resetInactivityTimer();
    return () => { if (inactivityTimer.current) clearTimeout(inactivityTimer.current); };
  }, [open, messages, resetInactivityTimer]);

  const send = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    if (view === "welcome") setView("chat");
    resetInactivityTimer();

    if (msg.length > 2000) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Please keep your message under 2000 characters." }]);
      return;
    }

    // Check message limit
    if (messages.length >= MAX_MESSAGES) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "We've had a great chat! To continue, feel free to start a new conversation or [book a free consultation](/contact)."
      }]);
      return;
    }

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      const current = assistantSoFar;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && prev.length > 1 && prev[prev.length - 2]?.role === "user") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: current } : m));
        }
        return [...prev, { role: "assistant", content: current }];
      });
    };

    try {
      await streamChat({
        message: msg,
        conversationId,
        onConversationId: (id) => setConversationId(id),
        onDelta: upsertAssistant,
        onDone: () => setLoading(false),
        onError: (errMsg) => {
          setMessages((prev) => [...prev, { role: "assistant", content: errMsg }]);
          setLoading(false);
        },
        signal: controller.signal,
      });
    } catch {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "I'm having a little trouble right now. Please try again or [book a free consultation](/contact)."
      }]);
      setLoading(false);
    }
  };

  const quickActions = [
    { label: "Our Services", icon: Briefcase, action: () => send("What services do you offer?") },
    { label: "Pricing", icon: DollarSign, action: () => send("How much does it cost?") },
    { label: "Book Consultation", icon: Phone, action: () => { navigate("/contact"); setOpen(false); } },
  ];

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-50 rounded-full w-12 h-12 flex items-center justify-center shadow-lg glow-shadow-sm hover:-translate-y-1 transition-all duration-300"
            style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
          >
            <MessageCircle className="h-6 w-6 text-white" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[360px] h-[500px] max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ background: "rgba(10, 14, 26, 0.95)", backdropFilter: "blur(24px)" }}
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between shrink-0 border-b border-border" style={{ background: "linear-gradient(135deg, rgba(0,217,255,0.15), rgba(79,70,229,0.15))" }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center font-heading font-bold text-sm text-white shadow-lg" style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>X</div>
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-sm leading-tight text-foreground">Xentic AI Assistant</span>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" /> Online</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-white/10">
                      <Menu className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-popover border-border z-[60]">
                    <DropdownMenuItem onClick={startNewChat} className="cursor-pointer gap-2">
                      <RotateCcw className="h-3.5 w-3.5" /> New Chat
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSoundOn(!soundOn)} className="cursor-pointer gap-2">
                      {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                      Sound {soundOn ? "On" : "Off"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setView("welcome")} className="cursor-pointer gap-2">
                      <Sparkles className="h-3.5 w-3.5" /> Home
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-white/10"><X className="h-4 w-4" /></button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {view === "welcome" ? (
                <motion.div
                  key="welcome"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-5"
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl" style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-foreground mb-1">Hey there! 👋</h3>
                    <p className="text-sm text-muted-foreground">I'm the Xentic AI assistant. How can I help you today?</p>
                  </div>
                  <button
                    onClick={startNewChat}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                  >
                    <MessageSquarePlus className="h-4 w-4" /> New Conversation
                  </button>
                  <div className="w-full space-y-2 mt-2">
                    {quickActions.map((qa) => (
                      <button
                        key={qa.label}
                        onClick={qa.action}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground hover:bg-white/5 transition-colors border border-border text-left"
                      >
                        <qa.icon className="h-4 w-4 text-primary shrink-0" />
                        {qa.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="chat"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 flex flex-col min-h-0"
                >
                  {/* Messages */}
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 scroll-smooth">
                    <AnimatePresence initial={false}>
                      {messages.map((m, i) => (
                        <motion.div
                          key={`${i}-${m.content.slice(0, 20)}`}
                          initial={{ opacity: 0, y: 8, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ duration: 0.2 }}
                          className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                              m.role === "user" ? "text-white" : "glass-card text-foreground"
                            }`}
                            style={m.role === "user" ? { background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" } : undefined}
                          >
                            <ReactMarkdown
                              components={{
                                a: ({ href, children }) => (
                                  <button onClick={() => { navigate(href || "/contact"); setOpen(false); }} className="underline font-semibold text-primary">
                                    {children}
                                  </button>
                                ),
                              }}
                            >
                              {m.content}
                            </ReactMarkdown>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {loading && messages[messages.length - 1]?.role !== "assistant" && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                        <div className="glass-card rounded-xl px-4 py-2 text-sm flex gap-1 items-center">
                          <span className="w-2 h-2 rounded-full bg-primary animate-bounce" />
                          <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0.15s" }} />
                          <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0.3s" }} />
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Input */}
                  <div className="border-t border-border p-3 flex gap-2 shrink-0">
                    <input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                      placeholder="Ask about our services..."
                      className="flex-1 bg-secondary rounded-lg px-3 py-2 text-sm outline-none placeholder:text-muted-foreground text-foreground border border-border focus:border-primary/50 transition-colors"
                      disabled={loading}
                    />
                    <Button
                      size="icon"
                      onClick={() => send()}
                      disabled={loading || !input.trim()}
                      className="border-0 shrink-0"
                      style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                    >
                      <Send className="h-4 w-4 text-white" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
